<c-container fluid>
  @if (error()) {
    <div class="alert alert-danger py-2 d-flex justify-content-between align-items-start gap-2">
      <span>{{ error() }}</span>
      <button type="button" class="btn-close" [attr.aria-label]="'appointments.admin.cancel' | translate"
              (click)="error.set(null)"></button>
    </div>
  }
  @if (notice()) {
    <div class="alert alert-success py-2 d-flex justify-content-between align-items-start gap-2">
      <span>{{ notice() }}</span>
      <button type="button" class="btn-close" [attr.aria-label]="'appointments.admin.cancel' | translate"
              (click)="notice.set(null)"></button>
    </div>
  }

  <c-card class="mb-4">
    <c-card-header class="d-flex justify-content-between align-items-center flex-wrap gap-2">
      <div>
        <strong>{{ 'appointments.admin.title' | translate }}</strong>
        <div class="small text-body-secondary">{{ 'appointments.admin.subtitle' | translate }}</div>
      </div>
      <button cButton color="primary" (click)="startCreate()">
        {{ 'appointments.admin.new' | translate }}
      </button>
    </c-card-header>

    <!-- ===================================================== edit form -->
    @if (showForm()) {
      <c-card-body class="border-bottom">
        <h6 class="mb-3">
          {{ (editing() ? 'appointments.admin.form.heading_edit' : 'appointments.admin.form.heading_new') | translate }}
          @if (editing(); as current) {
            <span class="text-body-secondary">#{{ current.id }}</span>
          }
        </h6>

        <!-- A — calendars -->
        <fieldset class="form-section">
          <legend class="form-section__legend">{{ 'appointments.admin.form.calendars' | translate }}</legend>
          <div class="d-flex flex-wrap gap-3">
            @for (calendar of calendars(); track calendar.id) {
              <div class="form-check">
                <input class="form-check-input" type="checkbox" [id]="'form-cal-' + calendar.id"
                       [checked]="formHasCalendar(calendar.id)" (change)="formToggleCalendar(calendar.id)" />
                <label class="form-check-label" [for]="'form-cal-' + calendar.id">{{ calendar.name }}</label>
              </div>
            }
          </div>
          <div class="form-text">{{ 'appointments.admin.form.calendars_hint' | translate }}</div>
        </fieldset>

        <!-- B — audiences -->
        <fieldset class="form-section">
          <legend class="form-section__legend">{{ 'appointments.admin.form.audiences' | translate }}</legend>
          <div class="form-check mb-2">
            <input class="form-check-input" type="checkbox" id="form-internal"
                   [checked]="form().internal_only" (change)="patch('internal_only', !form().internal_only)" />
            <label class="form-check-label fw-semibold" for="form-internal">
              {{ 'appointments.admin.internal_only' | translate }}
            </label>
          </div>
          <div class="role-grid" [class.role-grid--disabled]="form().internal_only">
            @for (role of roles(); track role.id) {
              <div class="form-check">
                <input class="form-check-input" type="checkbox" [id]="'form-role-' + role.id"
                       [disabled]="form().internal_only"
                       [checked]="formHasRole(role.id)" (change)="formToggleRole(role.id)" />
                <label class="form-check-label" [for]="'form-role-' + role.id">
                  {{ role.name }}
                  @if (role.automatic) {
                    <span class="text-body-secondary small">({{ 'appointments.admin.role_automatic' | translate }})</span>
                  }
                </label>
              </div>
            }
          </div>
          <div class="form-text">
            {{ (form().internal_only ? 'appointments.admin.form.internal_hint' : 'appointments.admin.form.audiences_hint') | translate }}
          </div>
        </fieldset>

        <c-row class="mb-3">
          <c-col xs="12" md="8">
            <label cLabel for="apptTitle">{{ 'appointments.admin.form.title' | translate }}</label>
            <input cFormControl id="apptTitle" type="text" maxlength="255"
                   [ngModel]="form().title" (ngModelChange)="patch('title', $event)" />
          </c-col>
          <c-col xs="12" md="4">
            <label cLabel for="apptLanguage">{{ 'appointments.admin.form.language' | translate }}</label>
            <select cSelect id="apptLanguage"
                    [ngModel]="form().language" (ngModelChange)="patch('language', $event)">
              @for (language of languages(); track language) {
                <option [value]="language">{{ language }}</option>
              }
              @if (form().language && !languages().includes(form().language)) {
                <option [value]="form().language">{{ form().language }}</option>
              }
            </select>
            <div class="form-text">{{ 'appointments.admin.form.language_hint' | translate }}</div>
          </c-col>
        </c-row>

        <c-row class="mb-3">
          <c-col xs="12">
            <label cLabel for="apptDetails">
              {{ 'appointments.admin.form.details' | translate }}
              <span class="text-body-secondary">({{ 'appointments.admin.form.optional' | translate }})</span>
            </label>
            <textarea cFormControl id="apptDetails" rows="4"
                      [ngModel]="form().details" (ngModelChange)="patch('details', $event)"></textarea>
          </c-col>
        </c-row>

        <!-- Translations: collapsible, one block per language; empty = falls back. -->
        @if (translationLanguages().length) {
          <div class="mb-3">
            <button type="button" cButton color="secondary" variant="outline" size="sm"
                    [attr.aria-expanded]="showTranslations()"
                    (click)="showTranslations.set(!showTranslations())">
              {{ (showTranslations() ? 'appointments.admin.form.translations_hide' : 'appointments.admin.form.translations_show') | translate }}
            </button>
            @if (showTranslations()) {
              <div class="form-text mb-2">{{ 'appointments.admin.form.translations_hint' | translate }}</div>
              @for (lang of translationLanguages(); track lang) {
                <details class="translation" [open]="hasTranslation(lang)">
                  <summary>
                    <span class="text-uppercase fw-semibold">{{ lang }}</span>
                    @if (hasTranslation(lang)) {
                      <c-badge color="success" class="ms-2">{{ 'appointments.admin.form.translation_filled' | translate }}</c-badge>
                    } @else {
                      <span class="text-body-secondary small ms-2">{{ 'appointments.admin.form.translation_fallback' | translate }}</span>
                    }
                  </summary>
                  <div class="translation__body">
                    <label cLabel [for]="'tr-title-' + lang">{{ 'appointments.admin.form.title' | translate }}</label>
                    <input cFormControl type="text" maxlength="255" [id]="'tr-title-' + lang"
                           [placeholder]="form().title"
                           [ngModel]="translationOf('title_i18n', lang)"
                           (ngModelChange)="setTranslation('title_i18n', lang, $event)" />
                    <label cLabel class="mt-2" [for]="'tr-details-' + lang">{{ 'appointments.admin.form.details' | translate }}</label>
                    <textarea cFormControl rows="3" [id]="'tr-details-' + lang"
                              [ngModel]="translationOf('details_i18n', lang)"
                              (ngModelChange)="setTranslation('details_i18n', lang, $event)"></textarea>
                  </div>
                </details>
              }
            }
          </div>
        }

        <c-row class="mb-3">
          <c-col xs="12" md="6">
            <label cLabel for="apptLocation">
              {{ 'appointments.location' | translate }}
              <span class="text-body-secondary">({{ 'appointments.admin.form.optional' | translate }})</span>
            </label>
            <input cFormControl id="apptLocation" type="text" maxlength="255"
                   [placeholder]="'appointments.admin.form.location_placeholder' | translate"
                   [ngModel]="form().location" (ngModelChange)="patch('location', $event)" />
          </c-col>
          <c-col xs="12" md="6">
            <label cLabel for="apptJoinUrl">
              {{ 'appointments.admin.form.join_url' | translate }}
              <span class="text-body-secondary">({{ 'appointments.admin.form.optional' | translate }})</span>
            </label>
            <input cFormControl id="apptJoinUrl" type="url" placeholder="https://zoom.us/j/..."
                   [ngModel]="form().join_url" (ngModelChange)="patch('join_url', $event)" />
          </c-col>
        </c-row>

        <c-row class="mb-3">
          <c-col xs="12" md="3">
            <label cLabel for="apptDate">{{ 'appointments.admin.form.date' | translate }}</label>
            <input cFormControl id="apptDate" type="date"
                   [ngModel]="form().local_date" (ngModelChange)="patch('local_date', $event)" />
          </c-col>
          <c-col xs="6" md="2">
            <label cLabel for="apptTime">{{ 'appointments.admin.form.time' | translate }}</label>
            <input cFormControl id="apptTime" type="time"
                   [ngModel]="form().local_time" (ngModelChange)="patch('local_time', $event)" />
          </c-col>
          <c-col xs="6" md="3">
            <label cLabel for="apptDuration">{{ 'appointments.admin.form.duration' | translate }}</label>
            <input cFormControl id="apptDuration" type="number" min="1" max="1440"
                   [ngModel]="form().duration_minutes" (ngModelChange)="patch('duration_minutes', $event)" />
          </c-col>
          <c-col xs="12" md="4">
            <label cLabel for="apptZone">{{ 'appointments.admin.form.time_zone' | translate }}</label>
            <select cSelect id="apptZone"
                    [ngModel]="form().time_zone" (ngModelChange)="patch('time_zone', $event)">
              @for (zone of timeZones(); track zone) {
                <option [value]="zone">{{ zone }}</option>
              }
            </select>
          </c-col>
        </c-row>

        <c-row class="mb-3">
          <c-col xs="12">
            <div class="form-text mt-0">{{ 'appointments.admin.form.time_zone_hint' | translate }}</div>
          </c-col>
        </c-row>

        <c-row class="mb-3">
          <c-col xs="12" md="4">
            <label cLabel for="apptRule">{{ 'appointments.admin.form.recurrence' | translate }}</label>
            <select cSelect id="apptRule"
                    [ngModel]="form().recurrence_rule" (ngModelChange)="patch('recurrence_rule', $event)">
              @for (rule of rules; track rule) {
                <option [value]="rule">{{ 'appointments.recurrence.' + rule | translate }}</option>
              }
            </select>
          </c-col>

          @if (showRecurrenceOptions()) {
            <c-col xs="12" md="4">
              <label cLabel for="apptUntil">
                {{ 'appointments.admin.form.until' | translate }}
                <span class="text-body-secondary">({{ 'appointments.admin.form.optional' | translate }})</span>
              </label>
              <input cFormControl id="apptUntil" type="date"
                     [ngModel]="form().recurrence_until" (ngModelChange)="patch('recurrence_until', $event)" />
              <div class="form-text">{{ 'appointments.admin.form.until_hint' | translate }}</div>
            </c-col>
          }
        </c-row>

        @if (showWeekdays()) {
          <c-row class="mb-3">
            <c-col xs="12">
              <label cLabel>{{ 'appointments.admin.form.weekdays' | translate }}</label>
              <div class="d-flex flex-wrap gap-3">
                @for (day of weekdayOrder; track day) {
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" [id]="'weekday-' + day"
                           [checked]="hasWeekday(day)" (change)="toggleWeekday(day)" />
                    <label class="form-check-label" [for]="'weekday-' + day">
                      {{ 'appointments.weekday.d' + day | translate }}
                    </label>
                  </div>
                }
              </div>
              <div class="form-text">{{ 'appointments.admin.form.weekdays_hint' | translate }}</div>
            </c-col>
          </c-row>
        }

        <c-row class="mb-3">
          <c-col xs="12">
            <div class="form-check">
              <input class="form-check-input" type="checkbox" id="apptActive"
                     [checked]="form().active" (change)="patch('active', !form().active)" />
              <label class="form-check-label" for="apptActive">
                {{ 'appointments.admin.form.active' | translate }}
              </label>
            </div>
            <div class="form-check mt-2">
              <input class="form-check-input" type="checkbox" id="apptPublishNews"
                     [checked]="form().publish_to_news" (change)="patch('publish_to_news', !form().publish_to_news)" />
              <label class="form-check-label" for="apptPublishNews">
                {{ 'appointments.admin.form.publish_to_news' | translate }}
              </label>
              <div class="form-text">{{ 'appointments.admin.form.publish_to_news_hint' | translate }}</div>
            </div>
          </c-col>
        </c-row>

        @if (!editing()) {
          <div class="form-text mb-2">{{ 'appointments.admin.form.draft_hint' | translate }}</div>
        }

        <div class="d-flex gap-2">
          <button cButton color="primary" [disabled]="saving()" (click)="save()">
            {{ 'appointments.admin.save' | translate }}
          </button>
          <button cButton color="secondary" variant="outline" [disabled]="saving()" (click)="cancel()">
            {{ 'appointments.admin.cancel' | translate }}
          </button>
        </div>
      </c-card-body>
    }

    <!-- ===================================================== cancel panel -->
    @if (cancelDraft(); as draft) {
      <c-card-body class="border-bottom cancel-panel">
        <h6 class="mb-2">{{ 'appointments.admin.cancel_panel.title' | translate }}: {{ draft.appointment.title }}</h6>

        @if (draft.appointment.cancelled_at) {
          <div class="mb-2">
            <c-badge color="danger">{{ 'appointments.admin.status.cancelled' | translate }}</c-badge>
            <span class="ms-2 small">{{ draft.appointment.cancellation_note }}</span>
            <button cButton color="secondary" variant="outline" size="sm" class="ms-2"
                    [disabled]="saving()" (click)="uncancel(draft.appointment)">
              {{ 'appointments.admin.cancel_panel.undo_series' | translate }}
            </button>
          </div>
        }

        @if (draft.appointment.cancelled_occurrences.length) {
          <div class="mb-2 small">
            {{ 'appointments.admin.cancel_panel.cancelled_dates' | translate }}:
            @for (date of draft.appointment.cancelled_occurrences; track date) {
              <span class="cancelled-date">
                {{ date }}
                <button type="button" class="btn btn-link btn-sm p-0 ms-1"
                        [disabled]="saving()" (click)="uncancel(draft.appointment, date)">
                  {{ 'appointments.admin.cancel_panel.undo' | translate }}
                </button>
              </span>
            }
          </div>
        }

        @if (!draft.appointment.cancelled_at) {
          <div class="d-flex flex-wrap gap-3 mb-2">
            <div class="form-check">
              <input class="form-check-input" type="radio" name="cancelScope" id="cancelScopeSeries"
                     [checked]="draft.scope === 'series'" (change)="patchCancel('scope', 'series')" />
              <label class="form-check-label" for="cancelScopeSeries">
                {{ (draft.appointment.recurrence_rule === 'none' ? 'appointments.admin.cancel_panel.scope_single' : 'appointments.admin.cancel_panel.scope_series') | translate }}
              </label>
            </div>
            @if (draft.appointment.recurrence_rule !== 'none') {
              <div class="form-check">
                <input class="form-check-input" type="radio" name="cancelScope" id="cancelScopeOccurrence"
                       [checked]="draft.scope === 'occurrence'" (change)="patchCancel('scope', 'occurrence')" />
                <label class="form-check-label" for="cancelScopeOccurrence">
                  {{ 'appointments.admin.cancel_panel.scope_occurrence' | translate }}
                </label>
              </div>
            }
          </div>

          <c-row class="mb-2">
            @if (draft.scope === 'occurrence') {
              <c-col xs="12" md="4">
                <label cLabel for="cancelDate">{{ 'appointments.admin.cancel_panel.date' | translate }}</label>
                <input cFormControl id="cancelDate" type="date"
                       [ngModel]="draft.occurrenceDate" (ngModelChange)="patchCancel('occurrenceDate', $event)" />
                <div class="form-text">
                  {{ 'appointments.admin.cancel_panel.date_hint' | translate: { zone: draft.appointment.time_zone } }}
                </div>
              </c-col>
            }
            <c-col xs="12" [md]="draft.scope === 'occurrence' ? 8 : 12">
              <label cLabel for="cancelNote">{{ 'appointments.admin.cancel_panel.note' | translate }}</label>
              <textarea cFormControl id="cancelNote" rows="2" maxlength="1000"
                        [placeholder]="'appointments.admin.cancel_panel.note_placeholder' | translate"
                        [ngModel]="draft.note" (ngModelChange)="patchCancel('note', $event)"></textarea>
            </c-col>
          </c-row>
          <div class="form-text mb-2">{{ 'appointments.admin.cancel_panel.hint' | translate }}</div>
        }

        <div class="d-flex gap-2">
          @if (!draft.appointment.cancelled_at) {
            <button cButton color="danger" [disabled]="saving()" (click)="confirmCancel()">
              {{ 'appointments.admin.cancel_panel.confirm' | translate }}
            </button>
          }
          <button cButton color="secondary" variant="outline" [disabled]="saving()" (click)="closeCancel()">
            {{ 'appointments.admin.cancel_panel.close' | translate }}
          </button>
        </div>
      </c-card-body>
    }

    <!-- ===================================================== filters -->
    <c-card-body class="border-bottom filters">
      <div class="filters__row">
        <div class="filters__field">
          <label cLabel class="small mb-1" for="filterCalendar">{{ 'appointments.admin.filter.calendar' | translate }}</label>
          <select cSelect size="sm" id="filterCalendar"
                  [ngModel]="filterCalendar()" (ngModelChange)="onFilterCalendar($event)">
            <option value="">{{ 'appointments.admin.filter_all' | translate }}</option>
            @for (calendar of calendars(); track calendar.id) {
              <option [value]="'' + calendar.id">{{ calendar.name }}</option>
            }
          </select>
        </div>
        <div class="filters__field">
          <label cLabel class="small mb-1" for="filterRole">{{ 'appointments.admin.filter.role' | translate }}</label>
          <select cSelect size="sm" id="filterRole"
                  [ngModel]="filterRole()" (ngModelChange)="onFilterRole($event)">
            <option value="">{{ 'appointments.admin.filter_all' | translate }}</option>
            @for (role of roles(); track role.id) {
              <option [value]="role.key">{{ role.name }}</option>
            }
          </select>
        </div>
        <div class="filters__field">
          <label cLabel class="small mb-1" for="filterStatus">{{ 'appointments.admin.filter.status' | translate }}</label>
          <select cSelect size="sm" id="filterStatus"
                  [ngModel]="filterStatus()" (ngModelChange)="onFilterStatus($event)">
            @for (status of statuses; track status) {
              <option [value]="status">
                {{ (status === 'all' ? 'appointments.admin.filter_all' : 'appointments.admin.status.' + status) | translate }}
              </option>
            }
          </select>
        </div>
        <div class="filters__field filters__field--grow">
          <label cLabel class="small mb-1" for="filterQuery">{{ 'appointments.admin.filter.search' | translate }}</label>
          <input cFormControl sizing="sm" id="filterQuery" type="search"
                 [placeholder]="'appointments.admin.filter.search_placeholder' | translate"
                 [ngModel]="filterQuery()" (ngModelChange)="onFilterQuery($event)" />
        </div>
        <div class="filters__field filters__field--action">
          <button cButton color="success" size="sm" [disabled]="selectedDraftCount() === 0" (click)="releaseSelected()">
            {{ 'appointments.admin.release_selected' | translate: { count: selectedDraftCount() } }}
          </button>
        </div>
      </div>
    </c-card-body>

    <!-- ===================================================== table -->
    <c-card-body>
      @if (loading() && appointments().length === 0) {
        <div class="text-center py-4">
          <div class="spinner-border text-primary" role="status"></div>
        </div>
      } @else if (appointments().length === 0) {
        <p class="text-body-secondary mb-0">{{ 'appointments.admin.empty' | translate }}</p>
      } @else {
        <div class="table-responsive">
          <table cTable hover class="align-middle appointments-table" [class.opacity-50]="loading()">
            <thead>
              <tr>
                <th class="col-select">
                  <input class="form-check-input" type="checkbox"
                         [attr.aria-label]="'appointments.admin.select_all' | translate"
                         [checked]="allSelected()" (change)="toggleSelectAll()" />
                </th>
                <th>{{ 'appointments.admin.col.appointment' | translate }}</th>
                <th>{{ 'appointments.admin.col.series' | translate }}</th>
                <th>{{ 'appointments.admin.col.calendars' | translate }}</th>
                <th>{{ 'appointments.admin.col.audiences' | translate }}</th>
                <th>{{ 'appointments.admin.col.status' | translate }}</th>
                <th class="text-end">{{ 'appointments.admin.col.actions' | translate }}</th>
              </tr>
            </thead>
            <tbody>
              @for (appointment of appointments(); track appointment.id) {
                <tr [class.row-busy]="isBusy(appointment)">
                  <td class="col-select">
                    <input class="form-check-input" type="checkbox"
                           [attr.aria-label]="appointment.title"
                           [checked]="isSelected(appointment)" (change)="toggleSelected(appointment)" />
                  </td>

                  <!-- Appointment: title + next occurrence in its zone AND in Europe/Berlin -->
                  <td class="col-appointment">
                    <div class="fw-semibold">
                      <span class="text-body-secondary small">#{{ appointment.id }}</span>
                      {{ appointment.title }}
                    </div>
                    @if (nextInOwnZone(appointment); as own) {
                      <div class="small text-nowrap">{{ own }} <span class="text-body-secondary">{{ appointment.time_zone }}</span></div>
                      @if (nextInReferenceZone(appointment); as reference) {
                        <div class="small text-nowrap text-body-secondary">= {{ reference }} {{ referenceTimeZone }}</div>
                      }
                    } @else {
                      <div class="small text-body-secondary">{{ 'appointments.admin.no_next_occurrence' | translate }}</div>
                    }
                    @if (appointment.location) {
                      <div class="small text-body-secondary">{{ 'appointments.location' | translate }}: {{ appointment.location }}</div>
                    }
                    <div class="d-flex flex-wrap gap-1 mt-1">
                      <span class="small text-uppercase text-body-secondary">{{ appointment.language }}</span>
                      @if (appointment.join_url) {
                        <a class="small" [href]="appointment.join_url" target="_blank" rel="noopener noreferrer">
                          {{ 'appointments.join' | translate }}
                        </a>
                      }
                      @for (thread of appointment.news_threads; track thread.config_domain_id) {
                        <a [routerLink]="newsThreadLink(thread.thread_id)" class="text-decoration-none"
                           [title]="calendarName(thread.config_domain_id)">
                          <c-badge color="info" textColor="dark">
                            {{ 'appointments.admin.form.news_badge' | translate }} · {{ calendarName(thread.config_domain_id) }}
                          </c-badge>
                        </a>
                      }
                    </div>
                  </td>

                  <!-- Series in plain words -->
                  <td class="small col-series">
                    {{ seriesSummary(appointment) }}
                    @if (appointment.duration_minutes) {
                      <div class="text-body-secondary">{{ appointment.duration_minutes }} min</div>
                    }
                    @if (appointment.cancelled_occurrences.length) {
                      <div class="text-danger">
                        {{ 'appointments.admin.cancelled_dates' | translate }}: {{ appointment.cancelled_occurrences.join(', ') }}
                      </div>
                    }
                  </td>

                  <!-- A — calendars, inline -->
                  <td class="col-calendars">
                    @for (calendar of calendars(); track calendar.id) {
                      <div class="form-check form-check-sm">
                        <input class="form-check-input" type="checkbox"
                               [id]="'cal-' + appointment.id + '-' + calendar.id"
                               [disabled]="isBusy(appointment)"
                               [checked]="hasCalendar(appointment, calendar.id)"
                               (change)="toggleCalendar(appointment, calendar.id, $event)" />
                        <label class="form-check-label small text-nowrap" [for]="'cal-' + appointment.id + '-' + calendar.id">
                          {{ calendar.name }}
                        </label>
                      </div>
                    }
                  </td>

                  <!-- B — audiences, inline -->
                  <td class="col-audiences">
                    @if (appointment.internal_only) {
                      <c-badge color="dark">{{ 'appointments.admin.internal_only' | translate }}</c-badge>
                    } @else {
                      <div class="d-flex flex-wrap gap-1">
                        @for (chip of audienceChips(appointment); track chip.id) {
                          <span class="audience-chip">{{ chip.name }}</span>
                        }
                      </div>
                    }
                    <button type="button" class="btn btn-link btn-sm p-0 mt-1"
                            [attr.aria-expanded]="audiencePickerFor() === appointment.id"
                            (click)="toggleAudiencePicker(appointment)">
                      {{ (audiencePickerFor() === appointment.id ? 'appointments.admin.audiences_close' : 'appointments.admin.audiences_change') | translate }}
                    </button>
                    @if (audiencePickerFor() === appointment.id) {
                      <div class="audience-picker">
                        <div class="form-check form-check-sm">
                          <input class="form-check-input" type="checkbox" [id]="'int-' + appointment.id"
                                 [disabled]="isBusy(appointment)"
                                 [checked]="appointment.internal_only"
                                 (change)="toggleInternal(appointment, $event)" />
                          <label class="form-check-label small fw-semibold" [for]="'int-' + appointment.id">
                            {{ 'appointments.admin.internal_only' | translate }}
                          </label>
                        </div>
                        <hr class="my-1" />
                        @for (role of roles(); track role.id) {
                          <div class="form-check form-check-sm">
                            <input class="form-check-input" type="checkbox"
                                   [id]="'role-' + appointment.id + '-' + role.id"
                                   [disabled]="isBusy(appointment) || appointment.internal_only"
                                   [checked]="hasAudience(appointment, role.id)"
                                   (change)="toggleAudience(appointment, role.id, $event)" />
                            <label class="form-check-label small" [for]="'role-' + appointment.id + '-' + role.id">
                              {{ role.name }}
                            </label>
                          </div>
                        }
                      </div>
                    }
                  </td>

                  <!-- Status -->
                  <td class="col-status">
                    <c-badge [color]="statusColor(appointment.status)">
                      {{ 'appointments.admin.status.' + appointment.status | translate }}
                    </c-badge>
                    @if (appointment.status === 'draft' && appointment.superseded_ids.length) {
                      <div class="small text-body-secondary mt-1">
                        {{ 'appointments.admin.replaces' | translate: { ids: idList(appointment.superseded_ids) } }}
                      </div>
                    }
                    @if (appointment.superseded_by_id) {
                      <div class="small text-body-secondary mt-1">
                        {{ 'appointments.admin.replaced_by' | translate: { id: '#' + appointment.superseded_by_id } }}
                      </div>
                    }
                    @if (appointment.released_at) {
                      <div class="small text-body-secondary mt-1">
                        {{ appointment.released_at | date: 'dd.MM.yyyy HH:mm' }}
                        @if (appointment.released_by_email) {
                          <br />{{ appointment.released_by_email }}
                        }
                      </div>
                    }
                  </td>

                  <!-- Actions -->
                  <td class="text-end col-actions">
                    <div class="d-flex flex-wrap justify-content-end gap-1">
                      @if (appointment.status === 'draft') {
                        <button cButton color="success" size="sm" [disabled]="isBusy(appointment)"
                                (click)="release(appointment)">
                          {{ 'appointments.admin.release' | translate }}
                        </button>
                      }
                      <button cButton color="secondary" variant="outline" size="sm"
                              (click)="startEdit(appointment)">
                        {{ 'appointments.admin.edit' | translate }}
                      </button>
                      <button cButton color="warning" variant="outline" size="sm"
                              (click)="startCancel(appointment)">
                        {{ 'appointments.admin.cancel_action' | translate }}
                      </button>
                      <button cButton color="danger" variant="outline" size="sm"
                              (click)="remove(appointment)">
                        {{ 'appointments.admin.delete' | translate }}
                      </button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </c-card-body>
  </c-card>
</c-container>
